/**
 * Offline mode, as a state the till is in rather than a fact about the network
 * (`08-pos-terminal-and-qr.md` § Offline mode).
 *
 * The source makes switching an explicit, confirmed decision in both
 * directions, and this copies that deliberately. A till that silently decided
 * it was offline would silently stop receiving other terminals' orders, and
 * the first anyone would know is a table waiting for food nobody was told
 * about.
 *
 * So: the network can *suggest*, and a person decides. What the network alone
 * does is drive the sync indicator.
 */

export type PosMode = "online" | "offline";

export type SyncState =
  | { readonly kind: "idle" }
  /** "Data is synchronizing…" — and the spec is clear this must not block use. */
  | { readonly kind: "syncing"; readonly remaining: number }
  | { readonly kind: "waiting"; readonly remaining: number; readonly retryAt: string }
  | { readonly kind: "needsAttention"; readonly failures: number };

/**
 * What offline actually costs, in the words a cashier needs.
 *
 * § Offline mode: "Offline supports basic functions only: ordering, offline
 * payment, receipt printing" and "Offline terminals cannot receive orders from
 * other terminals — this is the key limitation to communicate, since a QR order
 * placed while the till is offline will not appear."
 *
 * These are message keys rather than sentences: the till ships in English and
 * Khmer like everything else.
 */
export const OFFLINE_CAPABILITIES = {
  allowed: ["pos.offline.ordering", "pos.offline.payment", "pos.offline.printing"],
  withheld: [
    "pos.offline.noIncomingOrders",
    "pos.offline.noQrOrders",
    "pos.offline.noOnlinePayment",
    "pos.offline.noReports",
  ],
} as const;

export type ModeChange = {
  readonly from: PosMode;
  readonly to: PosMode;
  /** What the person confirming is being told they lose or regain. */
  readonly warnings: readonly string[];
  /** Going online with work still queued is a warning, not a refusal. */
  readonly queued: number;
};

export function describeChange(from: PosMode, to: PosMode, queued: number): ModeChange {
  if (from === to) return { from, to, warnings: [], queued };

  return {
    from,
    to,
    warnings:
      to === "offline"
        ? [...OFFLINE_CAPABILITIES.withheld]
        : queued > 0
          ? // Coming back online with a queue is normal, and the honest thing
            // to say is that it will take a moment rather than nothing at all.
            ["pos.offline.willSync"]
          : [],
    queued,
  };
}

/**
 * The indicator, derived rather than stored.
 *
 * A state machine somebody has to remember to update is a state machine that
 * lies, and this one is on screen the whole service.
 */
export function syncState(input: {
  mode: PosMode;
  queued: number;
  failures: number;
  isDraining: boolean;
  retryAt?: Date | null;
}): SyncState {
  if (input.failures > 0) return { kind: "needsAttention", failures: input.failures };
  if (input.queued === 0) return { kind: "idle" };
  if (input.isDraining) return { kind: "syncing", remaining: input.queued };
  return {
    kind: "waiting",
    remaining: input.queued,
    retryAt: (input.retryAt ?? new Date()).toISOString(),
  };
}
